import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const consumableReqAdapter = createEntityAdapter({})
const initialState = consumableReqAdapter.getInitialState()

export const consumableReqApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getConsumableReq: builder.query({
            query: () => ({
                url: '/consumreq',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedConsumableReq = responseData.map(consumableReq => {
                    consumableReq.id = consumableReq._id
                    return consumableReq
                });
                return consumableReqAdapter.setAll(initialState, loadedConsumableReq)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'ConsumableReq', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'ConsumableReq', id }))
                    ]
                } else return [{ type: 'ConsumableReq', id: 'LIST' }]
            }
        }),
        addNewConsumableReq: builder.mutation({
            query: initialUserData => ({
                url: '/consumreq',
                method: 'POST',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: [
                { type: 'ConsumableReq', id: "LIST" },
                { type: 'ConsumableUnreadCounts', id: 'LIST' }
            ]
        }),
        updateConsumableReq: builder.mutation({
            query: initialUserData => ({
                url: '/consumreq',
                method: 'PUT',
                body: {
                    ...initialUserData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'ConsumableReq', id: arg.id },
                { type: 'ConsumableUnreadCounts', id: 'LIST' }
            ]
        }),
        getFilteredConsumableRequests: builder.query({
            query: ({ page, limit, filters = {} }) => {
                // Convert arrays to comma-separated strings for backend
                const preparedFilters = {};
                for (const [key, value] of Object.entries(filters)) {
                    if (Array.isArray(value) && value.length > 0) {
                        preparedFilters[key] = value.join(',');
                    } else if (value !== '' && value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0)) {
                        preparedFilters[key] = value;
                    }
                }
                
                return {
                    url: "/consumreq/filtered",
                    params: { ...preparedFilters, page, limit },
                    validateStatus: (response) => response.status === 200,
                };
            },
            transformResponse: (responseData) => {
                const transformedData = {
                    requests: responseData.requests?.map((request) => ({
                        ...request,
                        id: request._id, // Ensure `id` is mapped correctly
                    })) || [],
                    totalFilteredRequests: responseData.totalFilteredRequests || 0,
                    totalPages: responseData.totalPages || 1,
                };
                
                return transformedData;
            },
            providesTags: (result) =>
                result?.requests
                ? [
                    { type: "ConsumableReq", id: "LIST" },
                    ...result.requests.map((request) => ({
                        type: "ConsumableReq",
                        id: request.id,
                    })),
                    ]
                : [{ type: "ConsumableReq", id: "LIST" }],
        }),
        // Mark a consumable request as read
        markConsumableAsRead: builder.mutation({
            query: ({ requestId, requestType }) => ({
                url: '/consumreq/mark-as-read',
                method: 'POST',
                body: {
                    requestId,
                    requestType
                }
            }),
            invalidatesTags: [
                { type: 'ConsumableReq', id: "LIST" },
                { type: 'ConsumableUnreadCounts', id: 'LIST' }
            ]
        }),
        getFilteredConsumableUnreadCounts: builder.query({
            query: ({ userId, sent_to_tags, req_store }) => ({
                url: '/consumreq/filtered-unread-counts',
                method: 'POST',
                body: {
                    userId,
                    sent_to_tags,
                    req_store
                }
            }),
            transformResponse: (responseData) => {
                return responseData.unreadCounts || {};
            },
            providesTags: [{ type: 'ConsumableUnreadCounts', id: 'LIST' }]
        }),
        markAllConsumableAsRead: builder.mutation({
            query: ({ userId, tabType, subTabType, requestType }) => ({
                url: '/consumreq/mark-all-read',
                method: 'POST',
                body: {
                    userId,
                    tabType,
                    subTabType,
                    requestType
                }
            }),
            invalidatesTags: [
                { type: 'ConsumableReq', id: "LIST" },
                { type: 'ConsumableUnreadCounts', id: 'LIST' }
            ]
        }),
        updateBoPartialConsumable: builder.mutation({
            query: ({ id, accept_time, status }) => ({
                url: '/consumreq/update-bo-partial',
                method: 'PUT',
                body: {
                    id,
                    accept_time,
                    status
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'ConsumableReq', id: arg.id }
            ]
        }),
    }),
})

export const {
    useGetConsumableReqQuery,
    useAddNewConsumableReqMutation,
    useUpdateConsumableReqMutation,
    useGetFilteredConsumableRequestsQuery,
    useLazyGetFilteredConsumableRequestsQuery, // ← Auto-generated lazy version
    useMarkConsumableAsReadMutation,
    useGetFilteredConsumableUnreadCountsQuery,
    useMarkAllConsumableAsReadMutation,
    useUpdateBoPartialConsumableMutation,
} = consumableReqApiSlice

// returns the query result object
export const selectConsumableReqResult = consumableReqApiSlice.endpoints.getConsumableReq.select()

// creates memoized selector
const selectConsumableReqData = createSelector(
    selectConsumableReqResult,
    consumableReqResult => consumableReqResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllConsumableReq,
    selectById: selectConsumableReqById,
    selectIds: selectConsumableReqIds
    // Pass in a selector that returns the users slice of state
} = consumableReqAdapter.getSelectors(state => selectConsumableReqData(state) ?? initialState) 